import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { Icon } from './Icon';
import api from '../../services/api';
import styles from './SearchSpotlight.module.css';

interface SearchResult {
  id: number;
  title: string;
  type: string;
  department_name?: string;
}

interface SearchApiItem {
  id: number;
  title: string;
  type: string;
  department?: { name?: string } | null;
}

export const SearchSpotlight: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
      setSelectedIdx(0);
    }
  }, [open]);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/documents/search', { params: { q } });
      const data = (res.data || []) as SearchApiItem[];
      setResults(data.map((d) => ({
        id: d.id,
        title: d.title,
        type: d.type,
        department_name: d.department?.name,
      })));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 200);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = results[selectedIdx];
      if (selected) {
        navigate(`/documents/${selected.id}`);
        setOpen(false);
      }
    }
  };

  const activeResultId = results[selectedIdx] ? `spotlight-result-${results[selectedIdx]!.id}` : undefined;

  if (!open) return null;

  return (
    <div className={styles['spotlight__overlay']} onClick={() => setOpen(false)}>
      <div
        className={styles['spotlight']}
        role="dialog"
        aria-modal="true"
        aria-label="Buscar documentos"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles['spotlight__input-wrapper']}>
          <Icon icon={faSearch} className={styles['spotlight__search-icon']} />
          <input
            ref={inputRef}
            type="text"
            className={styles['spotlight__input']}
            placeholder="Buscar documentos..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls="spotlight-results"
            aria-activedescendant={activeResultId}
            data-testid="spotlight-input"
          />
          <kbd className={styles['spotlight__esc-hint']}>ESC</kbd>
        </div>
        {loading && <div className={styles['spotlight__loading']}>Buscando...</div>}
        {!loading && query.length >= 2 && results.length === 0 && (
          <div className={styles['spotlight__empty']}>Sin resultados</div>
        )}
        {results.length > 0 && (
          <ul id="spotlight-results" role="listbox" className={styles['spotlight__results']}>
            {results.map((r, i) => (
              <li
                key={r.id}
                id={`spotlight-result-${r.id}`}
                role="option"
                aria-selected={i === selectedIdx}
                className={`${styles['spotlight__result']} ${i === selectedIdx ? styles['spotlight__result--active'] : ''}`}
                onClick={() => { navigate(`/documents/${r.id}`); setOpen(false); }}
                onMouseEnter={() => setSelectedIdx(i)}
                data-testid={`spotlight-result-${i}`}
                data-active={i === selectedIdx}
              >
                <div className={styles['spotlight__result-title']}>{r.title}</div>
                <div className={styles['spotlight__result-meta']}>
                  <span className={styles['spotlight__result-type']}>{r.type}</span>
                  {r.department_name && <span>{r.department_name}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
